import { useState, useEffect, useCallback, useRef } from "react";
import { namesAreReadable } from "@/engine/media/deviceChoice";

export interface MediaDevice {
  deviceId: string;
  label: string;
  /**
   * The group the browser puts the device in. Kept because it is what survives
   * an identifier rotation, and so what lets a remembered device be recognised
   * after site data was cleared (`src/engine/media/deviceChoice.ts`).
   */
  groupId: string;
}

/**
 * The browser hides the system default behind virtual `default`/`communications`
 * entries that duplicate a real device. Those are filtered out of the lists, so
 * the system choice is recovered here by matching the virtual entry back to its
 * real device — otherwise the panel would pre-select the first device in
 * enumeration order and contradict what the OS is actually playing through.
 */
function resolveSystemDefault(
  list: MediaDeviceInfo[],
  visible: MediaDevice[],
): string | undefined {
  const virtual = list.find((d) => d.deviceId === "default");
  if (!virtual) return visible[0]?.deviceId;

  const byGroup = visible.find((v) =>
    list.some(
      (d) =>
        d.deviceId === v.deviceId &&
        d.groupId !== "" &&
        d.groupId === virtual.groupId,
    ),
  );
  if (byGroup) return byGroup.deviceId;

  // Chrome labels the virtual entry "Default - <real device label>".
  const suffix = virtual.label.replace(/^Default\s*[-—]\s*/i, "").trim();
  const byLabel = suffix
    ? visible.find((v) => v.label.trim() === suffix)
    : undefined;
  return byLabel?.deviceId ?? visible[0]?.deviceId;
}

export function useMediaDevices() {
  const [audioInputs, setAudioInputs] = useState<MediaDevice[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDevice[]>([]);
  const [defaultAudioInput, setDefaultAudioInput] = useState<string>();
  const [defaultVideoInput, setDefaultVideoInput] = useState<string>();
  const [isLoaded, setIsLoaded] = useState(false);
  /**
   * Whether the browser is naming devices yet. Asked of the raw enumeration,
   * because the lists below fill an unnamed device in with "Microphone 1" — a
   * placeholder that would read as a name and let a remembered choice be
   * matched against an enumeration that says nothing.
   */
  const [namesReadable, setNamesReadable] = useState(false);

  const prevDevicesRef = useRef<string>("");

  const refresh = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Avoid unnecessary updates
      const devicesKey = devices
        .map((d) => `${d.kind}:${d.deviceId}`)
        .join(",");
      if (devicesKey === prevDevicesRef.current) return;
      prevDevicesRef.current = devicesKey;

      // Helper to filter unique devices - skip 'default' and 'communications' virtual devices
      // that duplicate real devices and cause selection issues
      const filterUnique = (
        list: MediaDeviceInfo[],
        fallbackPrefix: string,
      ) => {
        const seen = new Set<string>();
        const result: MediaDevice[] = [];
        let unnamedIndex = 0;

        for (const d of list) {
          // Skip virtual device IDs that just point to system defaults
          if (
            !d.deviceId ||
            d.deviceId === "default" ||
            d.deviceId === "communications"
          ) {
            continue;
          }

          if (seen.has(d.deviceId)) continue;
          seen.add(d.deviceId);

          unnamedIndex += 1;
          result.push({
            deviceId: d.deviceId,
            label: d.label || `${fallbackPrefix} ${unnamedIndex}`,
            groupId: d.groupId,
          });
        }

        return result;
      };

      const rawInputs = devices.filter((d) => d.kind === "audioinput");
      const rawVideo = devices.filter((d) => d.kind === "videoinput");

      const inputs = filterUnique(rawInputs, "Microphone");
      const cameras = filterUnique(rawVideo, "Camera");

      setAudioInputs(inputs);
      setVideoInputs(cameras);
      setDefaultAudioInput(resolveSystemDefault(rawInputs, inputs));
      setDefaultVideoInput(resolveSystemDefault(rawVideo, cameras));

      setNamesReadable(namesAreReadable(devices));
      setIsLoaded(true);
    } catch (error) {
      console.error("Failed to enumerate devices:", error);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      await refresh();
    })();

    const md = navigator.mediaDevices;
    md?.addEventListener?.("devicechange", refresh);
    return () => md?.removeEventListener?.("devicechange", refresh);
  }, [refresh]);

  return {
    audioInputs,
    videoInputs,
    defaultAudioInput,
    defaultVideoInput,
    isLoaded,
    namesReadable,
    refresh,
  };
}
